import I18n from 'I18n';
import Products from 'merchandise-lib/constants/Product';
import { fetchFeatureCategories } from './fetchFeatures';
import { NON_SUITE_PRODUCT_API_NAMES_BY_SKU_CATEGORY_NAME } from '../productApiNamesBySkuCategoryName';
import { logRavenException } from '../../errorUtils';
import { sortByProductLevel } from '../category-header/tableHeaderConfig';
const CRM_SUITE_PRODUCT_API_NAMES_TO_TIER = {
  'crm-suite-free-bundle': 'free',
  [Products.CRM_SUITE_STARTER_BUNDLE]: 'starter',
  [Products.CRM_SUITE_PROFESSIONAL_BUNDLE]: 'professional',
  [Products.CRM_SUITE_ENTERPRISE_BUNDLE]: 'enterprise'
};
export const fetchCrmSuiteFeatures = ({
  products,
  isPublicUseCase
}) => {
  const tiers = products.map(product => CRM_SUITE_PRODUCT_API_NAMES_TO_TIER[product]).sort(sortByProductLevel);
  const suiteFeaturesRecategorizedByHub = {
    categories: []
  };
  const featureCategoryPromiseBySkuCategory = [];
  const skuCategoryNames = Object.keys(NON_SUITE_PRODUCT_API_NAMES_BY_SKU_CATEGORY_NAME);
  const filteredSkuCategoryNames = [];
  for (const skuCategoryName of skuCategoryNames) {
    // Omit Commerce Hub if the upgrade is suite-starter because there
    // is no starter tier for this product line
    if (skuCategoryName === 'COMMERCE_PRODUCTS' && tiers[tiers.length - 1] === 'starter') {
      continue;
    } else {
      const productApiNamesBySkuCategoryName = NON_SUITE_PRODUCT_API_NAMES_BY_SKU_CATEGORY_NAME[skuCategoryName].filter(productApiName => {
        return tiers.some(tier => productApiName.includes(tier));
      });
      if (productApiNamesBySkuCategoryName.length > 0) {
        filteredSkuCategoryNames.push(skuCategoryName);
        featureCategoryPromiseBySkuCategory.push(fetchFeatureCategories({
          isPublicUseCase,
          productApiNames: productApiNamesBySkuCategoryName,
          skuCategoryName
        }));
      }
    }
  }
  return Promise.all(featureCategoryPromiseBySkuCategory).then(featureCategoryResponsesByHub => {
    featureCategoryResponsesByHub.forEach((featureCategoryResponses, skuCategoryNameIndex) => {
      const skuCategoryName = filteredSkuCategoryNames[skuCategoryNameIndex];
      const allFeaturesForSkuCategory = [];
      for (const {
        features
      } of featureCategoryResponses) {
        allFeaturesForSkuCategory.push(...features);
      }
      const totalFeatureCountForSkuCategory = allFeaturesForSkuCategory.reduce((sum, feature) => sum + feature.childrenFeatures.length + 1, 0);

      // Allow Data Hub to be displayed without the 5 feature minimum
      // https://hubspot.slack.com/archives/C02SM71SS5B/p1763394678184449?thread_ts=1763135001.112659&cid=C02SM71SS5B
      if (skuCategoryName !== 'OPERATIONS_HUB_PRODUCTS' && totalFeatureCountForSkuCategory < 5) {
        const notEnoughFeaturesError = new Error(`fetchCrmSuiteFeatures: Not enough features to display. Expected at least 5 features`);
        console.error(notEnoughFeaturesError);
        logRavenException({
          error: notEnoughFeaturesError,
          customMessage: notEnoughFeaturesError.message,
          additionalDetails: {
            skuCategoryName,
            totalFeatureCountForSkuCategory
          }
        });
      } else {
        suiteFeaturesRecategorizedByHub.categories.push({
          // Reset the category name to the product name
          category: {
            id: skuCategoryNameIndex + 1,
            sortOrder: skuCategoryNameIndex + 1,
            hub: getHubFromSkuCategoryName(skuCategoryName),
            categoryName: getProductNameFromSkuCategoryName(skuCategoryName)
          },
          features: allFeaturesForSkuCategory.map(feature => {
            return Object.assign({}, feature, {
              // Reset the associated products to the CRM suite equivalent so
              // it will match the logic to generate the column headers by CRM suite
              // productApiNames
              associatedProducts: getCrmSuiteProductEquivalentAssociatedProducts(feature.associatedProducts),
              featureDescription: getCrmSuiteLimitDescription(feature),
              childrenFeatures: feature.childrenFeatures.map(childFeature => Object.assign({}, childFeature, {
                associatedProducts: getCrmSuiteProductEquivalentAssociatedProducts(childFeature.associatedProducts),
                featureDescription: getCrmSuiteLimitDescription(childFeature)
              }))
            });
          })
        });
      }
    });
    return suiteFeaturesRecategorizedByHub.categories;
  });
};
function getCrmSuiteProductApiName(productApiName) {
  if (productApiName.includes('free')) {
    return 'crm-suite-free-bundle';
  } else if (productApiName.includes('starter')) {
    return Products.CRM_SUITE_STARTER_BUNDLE;
  } else if (productApiName.includes('professional')) {
    return Products.CRM_SUITE_PROFESSIONAL_BUNDLE;
  } else if (productApiName.includes('enterprise')) {
    return Products.CRM_SUITE_ENTERPRISE_BUNDLE;
  }
  return productApiName;
}
function getProductNameFromSkuCategoryName(skuCategoryName) {
  switch (skuCategoryName) {
    case 'MARKETING_PRODUCTS':
      return I18n.text('feature-monetization.products.marketing-hub');
    case 'SALES_PRODUCTS':
      return I18n.text('feature-monetization.products.sales-hub');
    case 'SERVICE_HUB_PRODUCTS':
      return I18n.text('feature-monetization.products.service-hub');
    case 'COMMERCE_PRODUCTS':
      return I18n.text('feature-monetization.products.commerce-hub');
    case 'CMS_HUB_PRODUCTS':
      return I18n.text('feature-monetization.products.cms-hub');
    case 'OPERATIONS_HUB_PRODUCTS':
      return I18n.text('feature-monetization.products.operations-hub');
    default:
      return skuCategoryName;
  }
}
function getCrmSuiteProductEquivalentAssociatedProducts(associatedProducts) {
  const transformedAssociatedProductsSet = new Set(associatedProducts.map(associatedProduct => getCrmSuiteProductApiName(associatedProduct)));
  return Array.from(transformedAssociatedProductsSet);
}
function getCrmSuiteLimitDescription(feature) {
  const updatedFeatureDescription = {
    featureDescription: feature.featureDescription.featureDescription,
    limitDescriptions: {
      limitDescriptionsByProduct: {}
    }
  };
  for (const [productApiName, limitDescription] of Object.entries(feature.featureDescription.limitDescriptions.limitDescriptionsByProduct)) {
    updatedFeatureDescription.limitDescriptions.limitDescriptionsByProduct[getCrmSuiteProductApiName(productApiName)] = limitDescription;
  }
  return updatedFeatureDescription;
}
function getHubFromSkuCategoryName(skuCategoryName) {
  switch (skuCategoryName) {
    case 'MARKETING_PRODUCTS':
      return 'MARKETING';
    case 'SALES_PRODUCTS':
      return 'SALES';
    case 'SERVICE_HUB_PRODUCTS':
      return 'SERVICE';
    case 'COMMERCE_PRODUCTS':
      return 'COMMERCE';
    case 'CMS_HUB_PRODUCTS':
      return 'CMS';
    case 'OPERATIONS_HUB_PRODUCTS':
      return 'OPERATIONS';
    default:
      return skuCategoryName;
  }
}